import graphqlClient from './graphqlClient';

/**
 * app.user_assignment service layer — drives which ultimate parents a `business_dev`/
 * `customer_success` user can access (their row-level scoping on app.customer_health,
 * see app_customer_health.yaml's `assignments.user_id` filter). Managed by
 * administrator/executive from the User List (see UserAssignmentsModal), mirroring the
 * "Add Ultimate Parent" picker on Customer Health (customerHealthService.ts) — see
 * `fetchTrackableParents` there, reused here for the parent search.
 */

export interface UserAssignment {
  id: string;
  ultimate_parent: string;
}

/** Ultimate parents already assigned to a user, newest first. */
export const fetchUserAssignments = async (userId: string): Promise<UserAssignment[]> => {
  const query = `
    query UserAssignments($userId: uuid!) {
      app_user_assignment(where: { user_id: { _eq: $userId } }, order_by: { created_at: desc }) {
        id
        ultimate_parent
      }
    }
  `;
  const resp = await graphqlClient.request<{ app_user_assignment: UserAssignment[] }>(query, { userId });
  return resp.app_user_assignment || [];
};

/** Grants a user access to an ultimate parent. on_conflict-do-nothing so re-assigning
 * an already-assigned parent is a no-op rather than a uniqueness-violation error. */
export const assignUserToParent = async (userId: string, ultimateParent: string): Promise<UserAssignment> => {
  const mutation = `
    mutation AssignUserToParent($userId: uuid!, $ultimateParent: String!) {
      insert_app_user_assignment_one(
        object: { user_id: $userId, ultimate_parent: $ultimateParent }
        on_conflict: { constraint: user_assignment_user_id_ultimate_parent_key, update_columns: [] }
      ) {
        id
        ultimate_parent
      }
    }
  `;
  const resp = await graphqlClient.request<{ insert_app_user_assignment_one: UserAssignment }>(
    mutation,
    { userId, ultimateParent }
  );
  return resp.insert_app_user_assignment_one;
};

/** Revokes a single assignment by its row id. */
export const unassignUserFromParent = async (assignmentId: string): Promise<void> => {
  const mutation = `
    mutation UnassignUserFromParent($id: uuid!) {
      delete_app_user_assignment_by_pk(id: $id) {
        id
      }
    }
  `;
  await graphqlClient.request(mutation, { id: assignmentId });
};
